import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreatorWork } from '@/api/member';

const fmtView = (n: number) => (n >= 10000 ? `${(n / 10000).toFixed(1)}万` : n.toLocaleString());
const fmtMoney = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

type Filter = 'all' | 'published' | 'review' | 'rejected';

const statusStyle: Record<CreatorWork['status'], string> = {
  published: 'bg-emerald-500/15 text-emerald-400',
  review: 'bg-amber-500/15 text-amber-400',
  approved: 'bg-sky-500/15 text-sky-400',
  transcoding: 'bg-sky-500/15 text-sky-400',
  ready: 'bg-cyan-500/15 text-cyan-400',
  transcode_failed: 'bg-rose-500/15 text-rose-400',
  publish_failed: 'bg-rose-500/15 text-rose-400',
  rejected: 'bg-rose-500/15 text-rose-400',
};

const statusLabelKey: Record<CreatorWork['status'], string> = {
  published: 'cinema_creatorStatusPublished',
  review: 'cinema_creatorStatusReview',
  approved: 'cinema_creatorStatusApproved',
  transcoding: 'cinema_creatorStatusTranscoding',
  ready: 'cinema_creatorStatusReady',
  transcode_failed: 'cinema_creatorStatusTranscodeFailed',
  publish_failed: 'cinema_creatorStatusPublishFailed',
  rejected: 'cinema_creatorStatusRejected',
};

export default function WorksPanel({
  works,
  onGoUpload,
}: {
  works: CreatorWork[];
  onGoUpload: () => void;
}) {
  const { t } = useTranslation('cinema');
  const [filter, setFilter] = useState<Filter>('all');

  const filters: { key: Filter; labelKey: string }[] = [
    { key: 'all', labelKey: 'cinema_filterAll' },
    { key: 'published', labelKey: 'cinema_creatorFilterPublished' },
    { key: 'review', labelKey: 'cinema_creatorFilterReview' },
    { key: 'rejected', labelKey: 'cinema_creatorFilterRejected' },
  ];

  const filtered = filter === 'all'
    ? works
    : works.filter((w) => filter === 'review'
      ? !['published', 'rejected'].includes(w.status)
      : w.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-accent-400">
            <i className="ri-movie-2-line text-lg"></i>
          </span>
          <h4 className="text-sm font-bold text-foreground-100">{t('cinema_creatorWorksTitle')}</h4>
          <span className="text-xs text-foreground-500">({works.length})</span>
        </div>
        <div className="flex items-center gap-1.5 bg-background-900/60 rounded-full p-1 w-fit">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === f.key
                  ? 'bg-accent-500 text-background-950'
                  : 'text-foreground-500 hover:text-foreground-300'
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-background-900/30 border border-background-800/40">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-foreground-50/5 mb-3">
            <i className="ri-movie-2-line text-2xl text-foreground-500"></i>
          </span>
          <p className="text-sm text-foreground-500 mb-4">{t('cinema_creatorNoWorks')}</p>
          <button
            onClick={onGoUpload}
            className="px-4 py-2 rounded-full text-xs font-semibold text-background-950 bg-accent-500 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('cinema_creatorGoUpload')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((w) => {
            const isPublished = w.status === 'published';
            return (
              <div
                key={w.id}
                className="rounded-2xl bg-background-900/50 border border-background-800/40 overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row gap-4 p-4 md:p-5">
                  <div className="flex-shrink-0 sm:w-28 flex items-center">
                    <img
                      src={w.coverUrl}
                      alt={w.title}
                      className="w-24 sm:w-full rounded-xl object-cover"
                      style={{ aspectRatio: '2/3' }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h5 className="text-sm md:text-base font-semibold text-foreground-100">{w.title}</h5>
                      {w.status === 'published' && <p className="mt-1 text-xs text-amber-400">作品分成比例：{(w.shareRatioBp ?? 0) / 100}%</p>}
                      {isPublished && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-background-950 bg-accent-500 whitespace-nowrap">
                          {t('cinema_creatorOriginal')}
                        </span>
                      )}
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${statusStyle[w.status]}`}
                      >
                        {t(statusLabelKey[w.status])}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap mt-2 text-[11px] text-foreground-500">
                      <span className="px-2 py-0.5 rounded bg-foreground-50/6 text-foreground-400 whitespace-nowrap">
                        {w.category}
                      </span>
                      <span>{t('cinema_episodes', { count: w.totalEpisodes })}</span>
                      <span className="hidden sm:inline text-foreground-700">·</span>
                      <span className="hidden sm:inline">
                        {isPublished
                          ? `${t('cinema_creatorPublishedOn')} ${w.publishedAt}`
                          : w.status === 'review'
                            ? `${t('cinema_creatorSubmittedOn')} ${w.submittedAt}`
                            : `${t('cinema_creatorRejectedTip')} ${w.submittedAt}`}
                      </span>
                    </div>

                    <p className="text-xs text-foreground-500 mt-2 line-clamp-2 hidden md:block">
                      {w.description}
                    </p>
                    {(w.status === 'transcode_failed' || w.status === 'publish_failed') && w.transcodeError && (
                      <p className="text-xs text-rose-400 mt-2 line-clamp-2">{w.transcodeError}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 divide-x divide-background-800/40 bg-background-950/40 border-t border-background-800/40">
                  <div className="px-3 py-3 text-center">
                    <p className="text-sm md:text-base font-bold text-foreground-100">{isPublished ? fmtView(w.views) : '—'}</p>
                    <p className="text-[10px] md:text-[11px] text-foreground-500 mt-0.5 flex items-center justify-center gap-1 whitespace-nowrap">
                      <i className="ri-eye-line text-accent-400"></i>
                      {t('cinema_creatorViews')}
                    </p>
                  </div>
                  <div className="px-3 py-3 text-center">
                    <p className="text-sm md:text-base font-bold text-foreground-100">{isPublished ? w.subscriptions.toLocaleString() : '—'}</p>
                    <p className="text-[10px] md:text-[11px] text-foreground-500 mt-0.5 flex items-center justify-center gap-1 whitespace-nowrap">
                      <i className="ri-user-heart-line text-amber-400"></i>
                      {t('cinema_creatorSubscriptions')}
                    </p>
                  </div>
                  <div className="px-3 py-3 text-center">
                    <p className="text-sm md:text-base font-bold text-emerald-400">{isPublished ? `¥${fmtMoney(w.revenue)}` : '—'}</p>
                    <p className="text-[10px] md:text-[11px] text-foreground-500 mt-0.5 flex items-center justify-center gap-1 whitespace-nowrap">
                      <i className="ri-money-cny-circle-line text-emerald-400"></i>
                      {t('cinema_creatorShare')}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
